import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bannerMediaUrl, fetchHomeBanner } from '@/lib/api';

const DEFAULT_HERO_BANNER = '/assets/images/c562dba6-0574-4bee-a9b6-c3968b8f16d1-1787485916196.png';
const BANNER_CACHE_KEY = 'awakasha_home_banner_url';
const SPARKLE_COLORS = ['#ebb413', '#ffffff', '#ffd700', '#fffacd', '#f5c518'];

function readCachedBanner() {
    try {
        return sessionStorage.getItem(BANNER_CACHE_KEY) || DEFAULT_HERO_BANNER;
    } catch {
        return DEFAULT_HERO_BANNER;
    }
}

function writeCachedBanner(url) {
    try {
        if (url) sessionStorage.setItem(BANNER_CACHE_KEY, url);
    } catch {
        // ignore storage failures
    }
}

function preloadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = reject;
        img.src = src;
    });
}

function generateSparkles(count) {
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        x: (i * 7.3 + 11) % 100,
        y: (i * 13.7 + 5) % 100,
        size: 4 + (i % 5) * 2,
        delay: (i * 0.37) % 3.5,
        duration: 1.8 + (i % 4) * 0.4,
        color: SPARKLE_COLORS[i % SPARKLE_COLORS.length],
    }));
}

const sparkles = generateSparkles(22);

function SparkleIcon({ size, color }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={color}
            xmlns="http://www.w3.org/2000/svg"
            style={{ filter: `drop-shadow(0 0 ${size / 2}px ${color})` }}
        >
            <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z" />
        </svg>
    );
}

export default function HeroSection() {
    const [mounted, setMounted] = useState(false);
    const [bannerSrc, setBannerSrc] = useState(readCachedBanner);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        let cancelled = false;

        fetchHomeBanner()
            .then(async (banner) => {
                const nextSrc = bannerMediaUrl(banner) || DEFAULT_HERO_BANNER;
                if (cancelled || !nextSrc || nextSrc === bannerSrc) {
                    if (nextSrc) writeCachedBanner(nextSrc);
                    return;
                }

                try {
                    await preloadImage(nextSrc);
                } catch {
                    // Still try to show the URL; browser may recover.
                }

                if (cancelled) return;
                writeCachedBanner(nextSrc);
                setBannerSrc(nextSrc);
            })
            .catch(() => {
                if (!cancelled && !bannerSrc) {
                    setBannerSrc(DEFAULT_HERO_BANNER);
                }
            });

        return () => {
            cancelled = true;
        };
        // Intentionally run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <section className="relative w-full min-h-screen overflow-hidden bg-[#0b1220]" aria-label="Hero banner">
            <style>{`
        @keyframes heroBannerZoom {
          0%   { transform: scale(1);    }
          50%  { transform: scale(1.06); }
          100% { transform: scale(1);    }
        }
        .hero-banner-img {
          animation: heroBannerZoom 12s ease-in-out infinite;
          transform-origin: center center;
        }
      `}</style>

            {bannerSrc ? (
                <img
                    key={bannerSrc}
                    src={bannerSrc}
                    alt="Hero banner"
                    className="hero-banner-img"
                    fetchPriority="high"
                    decoding="async"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                    }}
                />
            ) : null}

            {mounted && (
                <div className="absolute inset-0 pointer-events-none z-10" aria-hidden="true">
                    {sparkles.map((sparkle) => (
                        <motion.div
                            key={sparkle.id}
                            style={{
                                position: 'absolute',
                                left: `${sparkle.x}%`,
                                top: `${sparkle.y}%`,
                            }}
                            initial={{ opacity: 0, scale: 0, rotate: 0 }}
                            animate={{
                                opacity: [0, 1, 0.8, 1, 0],
                                scale: [0, 1, 0.7, 1.2, 0],
                                rotate: [0, 45, 90, 135, 180],
                                y: [0, -18, -36, -54, -72],
                                x: [0, sparkle.id % 2 === 0 ? 8 : -8, 0, sparkle.id % 2 === 0 ? -6 : 6, 0],
                            }}
                            transition={{
                                duration: sparkle.duration,
                                delay: sparkle.delay,
                                repeat: Infinity,
                                repeatDelay: 0.5 + (sparkle.id % 3) * 0.3,
                                ease: 'easeInOut',
                            }}
                        >
                            <SparkleIcon size={sparkle.size} color={sparkle.color} />
                        </motion.div>
                    ))}
                </div>
            )}

            <div className="absolute bottom-12 left-0 right-0 flex justify-center z-20">
                <Link
                    to="/events"
                    className="hover:bg-yellow-400 text-black font-bold text-xs px-6 py-2.5 rounded-full shadow-lg transition-all duration-300 hover:scale-105 tracking-widest uppercase"
                    style={{ backgroundColor: '#ebb413' }}
                >
                    BOOK MY SEAT
                </Link>
            </div>
        </section>
    );
}
